/**
 * Fait : la page de socle. Elle existe pour **exercer les cinq états** que toute
 *   surface du produit devra savoir montrer — nominal, vide, chargement, erreur,
 *   indisponible — et pour prouver qu'ils sont tous atteignables au clavier.
 *   C'est le critère de recette 4. Elle est bâtie sur les gabarits, ce qui les
 *   éprouve du même coup.
 *
 * Ne fait pas : elle n'appelle aucune API réelle, ne porte aucune donnée
 *   familiale, et ne préfigure pas l'interface finale. C'est un banc d'essai
 *   d'états, pas une maquette.
 */

import { useState } from 'react';

import { Annonce } from './gabarits/Annonce.tsx';
import { Champ } from './gabarits/Champ.tsx';
import { GabaritDePage } from './gabarits/GabaritDePage.tsx';

/** Les cinq états que toute surface doit savoir montrer. */
export const ETATS = ['nominal', 'vide', 'chargement', 'erreur', 'indisponible'] as const;

export type EtatDeSurface = (typeof ETATS)[number];

/** Libellé lisible de chaque état, pour les boutons. */
const LIBELLES: Readonly<Record<EtatDeSurface, string>> = {
  nominal: 'Nominal',
  vide: 'Vide',
  chargement: 'Chargement',
  erreur: 'Erreur',
  indisponible: 'Indisponible',
};

/** Ce que chaque état raconte à l'écran. */
function ContenuDeLEtat({ etat }: { etat: EtatDeSurface }) {
  switch (etat) {
    case 'nominal':
      return (
        <div>
          <h2>Trois projets partagés</h2>
          <ul>
            <li>Cuisine d’été — 2 personnes présentes</li>
            <li>Devis toiture — 1 personne présente</li>
            <li>Voyage de septembre — personne pour l’instant</li>
          </ul>
        </div>
      );

    case 'vide':
      return (
        <div>
          <h2>Aucun projet pour l’instant</h2>
          <p className="texte-attenue">
            Un projet est un espace partagé : on y travaille à plusieurs, en même temps.
          </p>
          <Champ
            libelle="Nom du projet"
            aide="Il sera visible par les membres que vous inviterez."
            name="nom-du-projet"
            data-gabarit="champ"
          />
          <Champ
            libelle="Nom du projet (exemple en erreur)"
            erreur="Un nom est requis."
            name="nom-du-projet-en-erreur"
            data-gabarit="champ-en-erreur"
          />
          <button type="button" data-role="principal">
            Créer un premier projet
          </button>
        </div>
      );

    case 'chargement':
      return (
        <div aria-busy="true">
          <h2>Chargement…</h2>
          <Annonce>Vos projets arrivent.</Annonce>
        </div>
      );

    case 'erreur':
      return (
        <div>
          <h2>Vos projets n’ont pas pu être chargés</h2>
          <Annonce urgence="urgent">La connexion a échoué. Rien n’a été perdu.</Annonce>
          <button type="button" data-role="principal">
            Réessayer
          </button>
        </div>
      );

    case 'indisponible':
      return (
        <div>
          <h2>L’atelier est momentanément fermé</h2>
          <Annonce>
            Une maintenance est en cours. Vos projets et vos documents sont intacts.
          </Annonce>
        </div>
      );
  }
}

/**
 * La page complète, avec son sélecteur d'état.
 *
 * Le sélecteur n'est pas un gadget de démonstration : il rend les cinq états
 * **atteignables au clavier**, ce qu'exige le critère 4. Sans lui, quatre états
 * sur cinq ne seraient jamais vus.
 */
export function PageSocle() {
  const [etat, setEtat] = useState<EtatDeSurface>('nominal');

  const commandes = ETATS.map((candidat) => (
    <li key={candidat}>
      <button
        type="button"
        aria-pressed={etat === candidat}
        data-role={etat === candidat ? 'principal' : undefined}
        data-etat={candidat}
        onClick={() => setEtat(candidat)}
      >
        {LIBELLES[candidat]}
      </button>
    </li>
  ));

  return (
    <GabaritDePage
      titre="Clannik"
      sousTitre="Page de socle — elle sert à exercer les cinq états, pas à montrer le produit."
      commandes={commandes}
      nomDesCommandes="États de la page"
    >
      <section className="carte" data-etat-courant={etat}>
        <ContenuDeLEtat etat={etat} />
      </section>
    </GabaritDePage>
  );
}
